// CognitiaIndia — Landing page persona caricatures (SVG).
// Phase 40.16 — hand-drawn illustrated cartoons of delighted Indian
// students, teachers and parents.  Not photos, not "trivial humanoids" —
// full-body caricatures with school uniforms, sarees, bindis, jhumkas,
// dupattas and big grins so the landing page feels warm and human.
import React from 'react';

// Shared color palettes
const SKIN_TONES = {
  girl: '#e5b48c',
  boy:  '#c58a63',
  saree1: '#e5b48c',
  saree2: '#d4926a',
  mom: '#e0a781',
  dad: '#b8825d',
};

// -----------------------------------------------------------------------------
// Priya — Class 12 exam-ace girl.  School uniform, plaited hair with red
// ribbon, braces-showing grin, textbook and stack of tags, thumbs up.
// -----------------------------------------------------------------------------
export const PriyaCaricature = () => (
  <svg viewBox="0 0 320 380" className="w-full h-full drop-shadow-2xl" aria-hidden data-testid="caricature-priya">
    <defs>
      <linearGradient id="pyBg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#4c1d95" />
        <stop offset="1" stopColor="#831843" />
      </linearGradient>
      <linearGradient id="pySkin" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#f0c8a3" />
        <stop offset="1" stopColor="#d69b73" />
      </linearGradient>
      <linearGradient id="pyHair" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#1a0f08" />
        <stop offset="1" stopColor="#2a1a10" />
      </linearGradient>
      <linearGradient id="pyShirt" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#f8fafc" />
        <stop offset="1" stopColor="#e2e8f0" />
      </linearGradient>
      <linearGradient id="pySkirt" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#0369a1" />
        <stop offset="1" stopColor="#0c4a6e" />
      </linearGradient>
    </defs>
    {/* halo of joy */}
    <circle cx="160" cy="180" r="180" fill="url(#pyBg)" opacity="0.35" />
    {[0, 40, 90, 140, 200, 260, 310].map((a, i) => (
      <g key={i} transform={`rotate(${a} 160 180)`}>
        <path d="M 160,10 L 156,30 L 164,30 Z" fill="#fbbf24" opacity="0.8" />
      </g>
    ))}
    {/* shadow */}
    <ellipse cx="160" cy="358" rx="90" ry="10" fill="#000" opacity="0.35" />

    {/* legs (skirt hem visible) */}
    <rect x="120" y="298" width="20" height="46" fill="url(#pySkin)" />
    <rect x="180" y="298" width="20" height="46" fill="url(#pySkin)" />
    <rect x="112" y="342" width="34" height="14" rx="3" fill="#0f172a" />
    <rect x="174" y="342" width="34" height="14" rx="3" fill="#0f172a" />
    {/* white socks */}
    <rect x="120" y="330" width="20" height="12" fill="#f8fafc" />
    <rect x="180" y="330" width="20" height="12" fill="#f8fafc" />

    {/* pleated skirt */}
    <path d="M 100,240 L 220,240 L 232,305 L 88,305 Z" fill="url(#pySkirt)" stroke="#0c4a6e" strokeWidth="1.2" />
    {[125, 145, 165, 185, 205].map((x, i) => (
      <line key={i} x1={x} y1="240" x2={x + (x - 160) * 0.35} y2="305" stroke="#155e88" strokeWidth="1" opacity="0.7" />
    ))}

    {/* white shirt tucked in */}
    <path d="M 96,180 Q 96,155 120,150 L 200,150 Q 224,155 224,180 L 224,246 L 96,246 Z" fill="url(#pyShirt)" stroke="#94a3b8" strokeWidth="1" />
    {/* school tie */}
    <path d="M 160,152 L 168,165 L 168,220 L 152,220 L 152,165 Z" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.8" />
    <path d="M 155,168 L 165,168 L 168,200 L 152,200 Z" fill="#fbbf24" opacity="0.5" />
    {/* school crest */}
    <circle cx="112" cy="200" r="6" fill="#fbbf24" stroke="#78350f" strokeWidth="0.6" />
    <text x="107" y="203" fontSize="6" fontWeight="900" fill="#78350f">CI</text>

    {/* arms — right raised thumbs up */}
    <path d="M 220,182 Q 260,150 268,110" stroke="url(#pyShirt)" strokeWidth="22" fill="none" strokeLinecap="round" />
    <circle cx="268" cy="110" r="12" fill="url(#pySkin)" />
    {/* thumb */}
    <path d="M 262,102 L 258,88 L 268,88 L 272,100 Z" fill="url(#pySkin)" stroke="#a16207" strokeWidth="0.6" />
    {/* left arm holding textbook */}
    <path d="M 100,182 Q 82,220 90,258" stroke="url(#pyShirt)" strokeWidth="22" fill="none" strokeLinecap="round" />
    <circle cx="88" cy="260" r="12" fill="url(#pySkin)" />
    <rect x="60" y="248" width="60" height="42" rx="2" fill="#0369a1" stroke="#0c4a6e" strokeWidth="1.4" transform="rotate(-8 90 269)" />
    <rect x="66" y="254" width="48" height="4" rx="1" fill="#fbbf24" transform="rotate(-8 90 269)" />
    <text x="70" y="278" fontSize="9" fontWeight="900" fill="#fbbf24" transform="rotate(-8 90 269)">PHYSICS 12</text>

    {/* head */}
    <ellipse cx="160" cy="120" rx="46" ry="52" fill="url(#pySkin)" />
    {/* neck */}
    <rect x="146" y="158" width="28" height="14" fill="url(#pySkin)" />
    {/* hair — parted middle, plaited on left side */}
    <path d="M 114,120 Q 105,55 160,52 Q 215,55 206,120 L 202,95 Q 190,72 160,72 Q 130,72 118,95 Z" fill="url(#pyHair)" />
    <path d="M 114,120 Q 108,175 128,215 L 138,205 Q 122,155 122,120 Z" fill="url(#pyHair)" />
    {/* red hair ribbon on plait */}
    <ellipse cx="128" cy="200" rx="9" ry="5" fill="#dc2626" />
    <path d="M 122,204 L 116,214 M 134,204 L 140,214" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
    {/* left plait */}
    <path d="M 208,120 Q 214,155 200,180" stroke="url(#pyHair)" strokeWidth="6" fill="none" strokeLinecap="round" />

    {/* face features */}
    {/* eyebrows */}
    <path d="M 138,108 Q 148,102 156,108" stroke="#0f172a" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M 164,108 Q 172,102 182,108" stroke="#0f172a" strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* eyes big & smiley */}
    <path d="M 138,120 Q 147,132 156,120" stroke="#0f172a" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    <path d="M 164,120 Q 173,132 182,120" stroke="#0f172a" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    {/* nose */}
    <path d="M 158,128 Q 156,138 160,142 Q 164,138 162,128" stroke="#a16207" strokeWidth="1.2" fill="none" />
    {/* mouth — big grin */}
    <path d="M 140,150 Q 160,168 180,150" stroke="#7f1d1d" strokeWidth="2.6" fill="#fbcfe8" strokeLinecap="round" />
    <path d="M 146,152 L 174,152 L 172,157 L 148,157 Z" fill="white" />
    <line x1="152" y1="152" x2="152" y2="158" stroke="#e5e7eb" strokeWidth="0.4" />
    <line x1="160" y1="152" x2="160" y2="158" stroke="#e5e7eb" strokeWidth="0.4" />
    <line x1="168" y1="152" x2="168" y2="158" stroke="#e5e7eb" strokeWidth="0.4" />
    {/* rosy cheeks */}
    <ellipse cx="128" cy="140" rx="7" ry="4" fill="#fb7185" opacity="0.55" />
    <ellipse cx="192" cy="140" rx="7" ry="4" fill="#fb7185" opacity="0.55" />
  </svg>
);

// -----------------------------------------------------------------------------
// Aarav — Class 10 coder boy.  School uniform (blue shirt + tie), messy hair,
// laptop in hand, super grin.
// -----------------------------------------------------------------------------
export const AaravCaricature = () => (
  <svg viewBox="0 0 320 380" className="w-full h-full drop-shadow-2xl" aria-hidden data-testid="caricature-aarav">
    <defs>
      <linearGradient id="avBg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#0e7490" />
        <stop offset="1" stopColor="#164e63" />
      </linearGradient>
      <linearGradient id="avSkin" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#e0a781" />
        <stop offset="1" stopColor="#b8825d" />
      </linearGradient>
      <linearGradient id="avHair" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#0f0a06" />
        <stop offset="1" stopColor="#2a1a10" />
      </linearGradient>
      <linearGradient id="avShirt" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#0284c7" />
        <stop offset="1" stopColor="#075985" />
      </linearGradient>
      <linearGradient id="avPant" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#334155" />
        <stop offset="1" stopColor="#1e293b" />
      </linearGradient>
    </defs>
    <circle cx="160" cy="180" r="180" fill="url(#avBg)" opacity="0.35" />
    <ellipse cx="160" cy="358" rx="90" ry="10" fill="#000" opacity="0.35" />

    {/* pants */}
    <rect x="115" y="256" width="34" height="86" fill="url(#avPant)" />
    <rect x="171" y="256" width="34" height="86" fill="url(#avPant)" />
    <rect x="110" y="340" width="42" height="14" rx="3" fill="#0f172a" />
    <rect x="168" y="340" width="42" height="14" rx="3" fill="#0f172a" />
    <line x1="132" y1="256" x2="132" y2="340" stroke="#1e293b" strokeWidth="0.8" />
    <line x1="188" y1="256" x2="188" y2="340" stroke="#1e293b" strokeWidth="0.8" />

    {/* shirt with tie */}
    <path d="M 96,180 Q 96,158 120,152 L 200,152 Q 224,158 224,180 L 224,258 L 96,258 Z" fill="url(#avShirt)" stroke="#075985" strokeWidth="1" />
    {/* tie */}
    <path d="M 160,155 L 168,168 L 168,225 L 152,225 L 152,168 Z" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.8" />
    <path d="M 155,171 L 165,171 L 168,205 L 152,205 Z" fill="#0f172a" opacity="0.3" />
    <rect x="150" y="152" width="20" height="10" fill="#0369a1" />
    {/* badge */}
    <rect x="105" y="195" width="24" height="10" rx="1" fill="#fbbf24" stroke="#78350f" strokeWidth="0.5" />
    <text x="108" y="203" fontSize="7" fontWeight="900" fill="#78350f">CI-10A</text>

    {/* arms — right hand holds laptop, left waves */}
    <path d="M 220,190 Q 244,210 244,246" stroke="url(#avShirt)" strokeWidth="22" fill="none" strokeLinecap="round" />
    <circle cx="244" cy="250" r="12" fill="url(#avSkin)" />
    {/* laptop */}
    <g transform="translate(214 232) rotate(6)">
      <rect x="0" y="0" width="66" height="42" rx="3" fill="#0f172a" stroke="#334155" strokeWidth="0.8" />
      <rect x="3" y="3" width="60" height="34" rx="2" fill="#020617" />
      <text x="7" y="14" fontSize="6" fill="#22c55e" fontFamily="monospace">$ python</text>
      <text x="7" y="21" fontSize="6" fill="#67e8f9" fontFamily="monospace">while True:</text>
      <text x="10" y="28" fontSize="6" fill="#fde68a" fontFamily="monospace">play(&amp;win)</text>
      <text x="7" y="35" fontSize="6" fill="#f9a8d4" fontFamily="monospace">🎮 LEVEL UP!</text>
      <rect x="-2" y="40" width="70" height="4" rx="1.5" fill="#475569" />
    </g>
    {/* left arm waving */}
    <path d="M 100,190 Q 68,166 66,124" stroke="url(#avShirt)" strokeWidth="22" fill="none" strokeLinecap="round" />
    <circle cx="66" cy="120" r="12" fill="url(#avSkin)" />
    {/* peace/victory fingers */}
    <path d="M 60,114 L 55,88 L 65,88 L 68,110 Z" fill="url(#avSkin)" stroke="#78350f" strokeWidth="0.5" />
    <path d="M 68,114 L 72,90 L 82,90 L 78,112 Z" fill="url(#avSkin)" stroke="#78350f" strokeWidth="0.5" />

    {/* head */}
    <ellipse cx="160" cy="118" rx="46" ry="52" fill="url(#avSkin)" />
    <rect x="146" y="156" width="28" height="14" fill="url(#avSkin)" />
    {/* messy hair */}
    <path d="M 114,120 Q 100,55 160,50 Q 220,58 206,120 L 200,96 Q 188,72 160,72 Q 132,72 120,96 Z" fill="url(#avHair)" />
    {[[125, 68],[140, 60],[155, 55],[172, 58],[188, 66]].map(([x, y], i) => (
      <path key={i} d={`M ${x},${y} L ${x + 6},${y - 12} L ${x + 12},${y}`} fill="url(#avHair)" />
    ))}
    {/* eyebrows raised */}
    <path d="M 136,102 Q 146,96 156,104" stroke="#0f172a" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <path d="M 164,104 Q 174,96 184,102" stroke="#0f172a" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    {/* eyes squinting-happy */}
    <path d="M 136,118 Q 146,130 156,118" stroke="#0f172a" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    <path d="M 164,118 Q 174,130 184,118" stroke="#0f172a" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    {/* nose */}
    <path d="M 158,124 Q 156,138 160,144 Q 164,138 162,124" stroke="#78350f" strokeWidth="1.2" fill="none" />
    {/* massive grin */}
    <path d="M 138,148 Q 160,172 182,148" stroke="#7f1d1d" strokeWidth="2.6" fill="#c1121f" strokeLinecap="round" />
    <path d="M 144,151 L 176,151 L 174,158 L 146,158 Z" fill="white" />
    <line x1="152" y1="151" x2="152" y2="158" stroke="#e5e7eb" strokeWidth="0.4" />
    <line x1="160" y1="151" x2="160" y2="158" stroke="#e5e7eb" strokeWidth="0.4" />
    <line x1="168" y1="151" x2="168" y2="158" stroke="#e5e7eb" strokeWidth="0.4" />
    {/* cheeks */}
    <ellipse cx="126" cy="136" rx="7" ry="4" fill="#fb7185" opacity="0.45" />
    <ellipse cx="194" cy="136" rx="7" ry="4" fill="#fb7185" opacity="0.45" />
  </svg>
);

// -----------------------------------------------------------------------------
// TeachersCaricature — 3 delighted Indian teachers in sarees + kurta.
// -----------------------------------------------------------------------------
export const TeachersCaricature = () => (
  <svg viewBox="0 0 480 380" className="w-full h-full drop-shadow-2xl" aria-hidden data-testid="caricature-teachers">
    <defs>
      <linearGradient id="tSari1" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#f97316" />
        <stop offset="1" stopColor="#7c2d12" />
      </linearGradient>
      <linearGradient id="tKurta" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#0284c7" />
        <stop offset="1" stopColor="#0c4a6e" />
      </linearGradient>
      <linearGradient id="tSari2" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#10b981" />
        <stop offset="1" stopColor="#065f46" />
      </linearGradient>
      <linearGradient id="tSkin1" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#e5b48c" />
        <stop offset="1" stopColor="#c58a63" />
      </linearGradient>
      <linearGradient id="tSkin2" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#d4926a" />
        <stop offset="1" stopColor="#a16207" />
      </linearGradient>
    </defs>

    {/* Teacher 1 — woman in orange saree with bindi, holding book */}
    <g transform="translate(20 60)">
      <ellipse cx="70" cy="298" rx="60" ry="8" fill="#000" opacity="0.3" />
      {/* saree pleats */}
      <path d="M 30,180 L 110,180 L 128,290 L 12,290 Z" fill="url(#tSari1)" stroke="#7c2d12" strokeWidth="1.2" />
      {[42, 62, 82, 102].map((x, i) => (
        <line key={i} x1={x} y1="180" x2={x + (x - 70) * 0.4} y2="290" stroke="#c2410c" strokeWidth="1" opacity="0.75" />
      ))}
      {/* blouse */}
      <path d="M 42,132 L 98,132 L 108,178 L 32,178 Z" fill="#7c2d12" />
      {/* dupatta over shoulder */}
      <path d="M 100,140 Q 128,160 132,200 L 140,240 L 122,244 L 116,208 Q 112,172 92,158 Z" fill="url(#tSari1)" opacity="0.85" stroke="#7c2d12" strokeWidth="0.8" />
      {/* head */}
      <ellipse cx="70" cy="100" rx="36" ry="40" fill="url(#tSkin1)" />
      <rect x="60" y="132" width="20" height="10" fill="url(#tSkin1)" />
      {/* hair with bun */}
      <path d="M 34,100 Q 30,54 70,50 Q 110,54 106,100 L 100,74 Q 88,60 70,60 Q 52,60 40,74 Z" fill="#1a0f08" />
      <circle cx="70" cy="52" r="12" fill="#0f0a06" />
      {/* red bindi */}
      <circle cx="70" cy="80" r="3" fill="#dc2626" />
      {/* jhumka earrings */}
      <circle cx="36" cy="112" r="3" fill="#fbbf24" stroke="#78350f" strokeWidth="0.4" />
      <circle cx="104" cy="112" r="3" fill="#fbbf24" stroke="#78350f" strokeWidth="0.4" />
      {/* eyes closed happy */}
      <path d="M 52,98 Q 60,108 66,98" stroke="#0f172a" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M 74,98 Q 82,108 88,98" stroke="#0f172a" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* smile */}
      <path d="M 56,120 Q 70,132 84,120" stroke="#7f1d1d" strokeWidth="2.2" fill="#c1121f" strokeLinecap="round" />
      <path d="M 60,122 L 80,122 L 78,127 L 62,127 Z" fill="white" />
      <ellipse cx="46" cy="116" rx="6" ry="3.5" fill="#fb7185" opacity="0.45" />
      <ellipse cx="94" cy="116" rx="6" ry="3.5" fill="#fb7185" opacity="0.45" />
      {/* arms — right holds a book */}
      <path d="M 108,180 Q 132,196 132,230" stroke="url(#tSari1)" strokeWidth="18" fill="none" strokeLinecap="round" />
      <circle cx="132" cy="234" r="10" fill="url(#tSkin1)" />
      <rect x="108" y="220" width="46" height="34" rx="1.5" fill="#0369a1" stroke="#0c4a6e" strokeWidth="1" transform="rotate(-6 131 237)" />
      <text x="112" y="240" fontSize="7" fontWeight="900" fill="#fbbf24" transform="rotate(-6 131 237)">GRAMMAR</text>
    </g>

    {/* Teacher 2 — male teacher with moustache in blue kurta, waving */}
    <g transform="translate(180 45)">
      <ellipse cx="70" cy="310" rx="66" ry="8" fill="#000" opacity="0.3" />
      {/* pants */}
      <rect x="30" y="212" width="30" height="90" fill="#78350f" />
      <rect x="80" y="212" width="30" height="90" fill="#78350f" />
      <rect x="26" y="300" width="38" height="10" rx="2" fill="#0f172a" />
      <rect x="76" y="300" width="38" height="10" rx="2" fill="#0f172a" />
      {/* long kurta */}
      <path d="M 24,140 Q 24,120 44,116 L 96,116 Q 116,120 116,140 L 120,230 L 20,230 Z" fill="url(#tKurta)" stroke="#0c4a6e" strokeWidth="1" />
      {/* kurta buttons */}
      {[136, 148, 160, 180, 200].map((y, i) => (
        <circle key={i} cx="70" cy={y} r="1.6" fill="#fde68a" />
      ))}
      {/* head */}
      <ellipse cx="70" cy="90" rx="38" ry="42" fill="url(#tSkin2)" />
      <rect x="60" y="124" width="20" height="12" fill="url(#tSkin2)" />
      {/* hair (short) */}
      <path d="M 32,88 Q 26,42 70,38 Q 114,42 108,88 L 100,64 Q 88,52 70,52 Q 52,52 40,64 Z" fill="#0f0a06" />
      {/* glasses */}
      <circle cx="55" cy="90" r="9" fill="none" stroke="#334155" strokeWidth="2" />
      <circle cx="85" cy="90" r="9" fill="none" stroke="#334155" strokeWidth="2" />
      <line x1="64" y1="90" x2="76" y2="90" stroke="#334155" strokeWidth="2" />
      {/* eyes */}
      <circle cx="55" cy="90" r="2" fill="#0f172a" />
      <circle cx="85" cy="90" r="2" fill="#0f172a" />
      <circle cx="56" cy="88" r="0.7" fill="white" />
      <circle cx="86" cy="88" r="0.7" fill="white" />
      {/* moustache */}
      <path d="M 54,110 Q 62,106 70,110 Q 78,106 86,110 Q 80,116 70,114 Q 60,116 54,110 Z" fill="#1a0f08" />
      {/* smile */}
      <path d="M 58,118 Q 70,128 82,118" stroke="#7f1d1d" strokeWidth="2.2" fill="#c1121f" strokeLinecap="round" />
      <path d="M 62,120 L 78,120 L 76,124 L 64,124 Z" fill="white" />
      <ellipse cx="46" cy="108" rx="5" ry="3" fill="#fb7185" opacity="0.4" />
      <ellipse cx="94" cy="108" rx="5" ry="3" fill="#fb7185" opacity="0.4" />
      {/* left arm on hip */}
      <path d="M 22,150 Q 4,180 22,214" stroke="url(#tKurta)" strokeWidth="20" fill="none" strokeLinecap="round" />
      <circle cx="22" cy="214" r="10" fill="url(#tSkin2)" />
      {/* right arm raised waving */}
      <path d="M 118,150 Q 152,120 148,74" stroke="url(#tKurta)" strokeWidth="20" fill="none" strokeLinecap="round" />
      <circle cx="148" cy="72" r="11" fill="url(#tSkin2)" />
      {/* chalk in hand */}
      <rect x="140" y="60" width="4" height="18" rx="1" fill="#f8fafc" stroke="#94a3b8" strokeWidth="0.5" transform="rotate(20 142 69)" />
    </g>

    {/* Teacher 3 — woman in green saree, dupatta, laughing */}
    <g transform="translate(340 65)">
      <ellipse cx="70" cy="292" rx="60" ry="8" fill="#000" opacity="0.3" />
      <path d="M 30,178 L 110,178 L 130,288 L 10,288 Z" fill="url(#tSari2)" stroke="#065f46" strokeWidth="1.2" />
      {[42, 62, 82, 102].map((x, i) => (
        <line key={i} x1={x} y1="178" x2={x + (x - 70) * 0.4} y2="288" stroke="#047857" strokeWidth="1" opacity="0.7" />
      ))}
      <path d="M 42,130 L 98,130 L 108,176 L 32,176 Z" fill="#065f46" />
      {/* golden dupatta border */}
      <path d="M 30,178 L 110,178 L 108,183 L 32,183 Z" fill="#fbbf24" opacity="0.85" />
      {/* head */}
      <ellipse cx="70" cy="100" rx="36" ry="40" fill="url(#tSkin1)" />
      <rect x="60" y="132" width="20" height="10" fill="url(#tSkin1)" />
      {/* hair */}
      <path d="M 34,100 Q 32,54 70,50 Q 108,54 106,100 L 96,74 Q 84,60 70,60 Q 56,60 44,74 Z" fill="#1a0f08" />
      {/* side braid */}
      <path d="M 106,102 Q 118,140 108,180" stroke="#1a0f08" strokeWidth="6" fill="none" strokeLinecap="round" />
      <ellipse cx="112" cy="180" rx="5" ry="3" fill="#fbbf24" />
      {/* bindi */}
      <circle cx="70" cy="82" r="3" fill="#dc2626" />
      {/* jhumka */}
      <path d="M 36,110 L 36,122 M 32,122 L 40,122" stroke="#fbbf24" strokeWidth="1.6" />
      <circle cx="36" cy="126" r="2.4" fill="#fbbf24" />
      <path d="M 104,110 L 104,122 M 100,122 L 108,122" stroke="#fbbf24" strokeWidth="1.6" />
      <circle cx="104" cy="126" r="2.4" fill="#fbbf24" />
      {/* eyes squinting laugh */}
      <path d="M 52,98 Q 60,108 66,98" stroke="#0f172a" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M 74,98 Q 82,108 88,98" stroke="#0f172a" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* laughing mouth */}
      <path d="M 54,118 Q 70,138 86,118" stroke="#7f1d1d" strokeWidth="2.4" fill="#c1121f" strokeLinecap="round" />
      <path d="M 60,120 L 80,120 L 78,132 L 62,132 Z" fill="white" />
      <ellipse cx="46" cy="118" rx="6" ry="3.5" fill="#fb7185" opacity="0.45" />
      <ellipse cx="94" cy="118" rx="6" ry="3.5" fill="#fb7185" opacity="0.45" />
    </g>
  </svg>
);

// -----------------------------------------------------------------------------
// Ritu — delighted mother in purple saree, phone in hand showing dashboard.
// -----------------------------------------------------------------------------
export const RituCaricature = () => (
  <svg viewBox="0 0 320 380" className="w-full h-full drop-shadow-2xl" aria-hidden data-testid="caricature-ritu">
    <defs>
      <linearGradient id="rSari" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#a855f7" />
        <stop offset="1" stopColor="#6b21a8" />
      </linearGradient>
      <linearGradient id="rBlouse" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#7e22ce" />
        <stop offset="1" stopColor="#581c87" />
      </linearGradient>
      <linearGradient id="rSkin" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#f0bc94" />
        <stop offset="1" stopColor="#d69265" />
      </linearGradient>
      <linearGradient id="rHair" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#1a0f08" />
        <stop offset="1" stopColor="#2a1a10" />
      </linearGradient>
      <linearGradient id="rBg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#065f46" />
        <stop offset="1" stopColor="#064e3b" />
      </linearGradient>
    </defs>
    <circle cx="160" cy="180" r="180" fill="url(#rBg)" opacity="0.35" />
    <ellipse cx="160" cy="358" rx="94" ry="10" fill="#000" opacity="0.35" />

    {/* saree pleats bottom */}
    <path d="M 88,220 L 232,220 L 258,346 L 62,346 Z" fill="url(#rSari)" stroke="#581c87" strokeWidth="1.4" />
    {[110, 138, 166, 194, 222].map((x, i) => (
      <line key={i} x1={x} y1="220" x2={x + (x - 160) * 0.4} y2="346" stroke="#7e22ce" strokeWidth="1.2" opacity="0.75" />
    ))}
    {/* golden pallu across chest */}
    <path d="M 100,160 Q 132,180 168,182 L 236,188 L 232,224 L 168,220 Q 128,222 96,196 Z" fill="url(#rSari)" opacity="0.9" stroke="#581c87" strokeWidth="0.8" />
    <path d="M 96,160 L 236,188" stroke="#fbbf24" strokeWidth="4" opacity="0.85" />
    {/* blouse */}
    <path d="M 96,150 L 224,150 L 232,222 L 88,222 Z" fill="url(#rBlouse)" />
    {/* head */}
    <ellipse cx="160" cy="105" rx="46" ry="52" fill="url(#rSkin)" />
    <rect x="146" y="142" width="28" height="14" fill="url(#rSkin)" />
    {/* hair — parted, small bun */}
    <path d="M 114,105 Q 108,42 160,38 Q 214,42 206,105 L 198,80 Q 184,60 160,60 Q 136,60 122,80 Z" fill="url(#rHair)" />
    <circle cx="160" cy="42" r="14" fill="#0f0a06" />
    <path d="M 160,55 L 175,90 L 145,90 Z" fill="#dc2626" opacity="0.65" />
    {/* red bindi */}
    <circle cx="160" cy="76" r="3.6" fill="#dc2626" />
    {/* jhumka earrings gold */}
    <path d="M 114,120 L 114,132" stroke="#fbbf24" strokeWidth="2" />
    <circle cx="114" cy="136" r="4" fill="#fbbf24" stroke="#78350f" strokeWidth="0.5" />
    <path d="M 206,120 L 206,132" stroke="#fbbf24" strokeWidth="2" />
    <circle cx="206" cy="136" r="4" fill="#fbbf24" stroke="#78350f" strokeWidth="0.5" />
    {/* nose ring hint */}
    <circle cx="152" cy="118" r="1.4" fill="#fbbf24" />
    {/* eyebrows */}
    <path d="M 132,93 Q 145,86 156,94" stroke="#0f172a" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M 164,94 Q 175,86 188,93" stroke="#0f172a" strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* eyes — bright */}
    <ellipse cx="144" cy="106" rx="4" ry="5" fill="#f8fafc" stroke="#0f172a" strokeWidth="0.8" />
    <ellipse cx="176" cy="106" rx="4" ry="5" fill="#f8fafc" stroke="#0f172a" strokeWidth="0.8" />
    <circle cx="144" cy="106" r="2.4" fill="#0f172a" />
    <circle cx="176" cy="106" r="2.4" fill="#0f172a" />
    <circle cx="145" cy="104" r="0.8" fill="white" />
    <circle cx="177" cy="104" r="0.8" fill="white" />
    {/* nose */}
    <path d="M 158,114 Q 156,128 160,132 Q 164,128 162,114" stroke="#a16207" strokeWidth="1.2" fill="none" />
    {/* big smile with teeth */}
    <path d="M 136,138 Q 160,158 184,138" stroke="#7f1d1d" strokeWidth="2.6" fill="#c1121f" strokeLinecap="round" />
    <path d="M 144,141 L 176,141 L 174,148 L 146,148 Z" fill="white" />
    <line x1="152" y1="141" x2="152" y2="148" stroke="#e5e7eb" strokeWidth="0.4" />
    <line x1="160" y1="141" x2="160" y2="148" stroke="#e5e7eb" strokeWidth="0.4" />
    <line x1="168" y1="141" x2="168" y2="148" stroke="#e5e7eb" strokeWidth="0.4" />
    {/* rosy cheeks */}
    <ellipse cx="122" cy="128" rx="8" ry="4.5" fill="#fb7185" opacity="0.55" />
    <ellipse cx="198" cy="128" rx="8" ry="4.5" fill="#fb7185" opacity="0.55" />

    {/* arms — right holds phone */}
    <path d="M 232,180 Q 260,210 254,258" stroke="url(#rSari)" strokeWidth="24" fill="none" strokeLinecap="round" />
    <circle cx="254" cy="262" r="12" fill="url(#rSkin)" />
    {/* phone showing dashboard */}
    <g transform="translate(226 244) rotate(10)">
      <rect x="0" y="0" width="34" height="60" rx="6" fill="#0f172a" stroke="#334155" strokeWidth="1.2" />
      <rect x="3" y="8" width="28" height="45" rx="1" fill="#020617" />
      <text x="6" y="16" fontSize="4.5" fill="#22c55e" fontFamily="monospace">CognitiaIndia</text>
      <rect x="6" y="20" width="22" height="3" rx="1" fill="#22c55e" opacity="0.35" />
      <rect x="6" y="20" width="18" height="3" rx="1" fill="#22c55e" />
      <text x="6" y="30" fontSize="4" fill="#67e8f9" fontFamily="monospace">Aarav · Cl 10</text>
      <rect x="6" y="34" width="22" height="2.6" rx="1" fill="#67e8f9" opacity="0.35" />
      <rect x="6" y="34" width="14" height="2.6" rx="1" fill="#67e8f9" />
      <text x="6" y="43" fontSize="4" fill="#fbbf24" fontFamily="monospace">Priya · Cl 12</text>
      <rect x="6" y="47" width="22" height="2.6" rx="1" fill="#fbbf24" opacity="0.35" />
      <rect x="6" y="47" width="20" height="2.6" rx="1" fill="#fbbf24" />
      <circle cx="17" cy="57" r="1" fill="#94a3b8" />
    </g>
    {/* left arm relaxed */}
    <path d="M 88,180 Q 68,214 78,258" stroke="url(#rSari)" strokeWidth="24" fill="none" strokeLinecap="round" />
    <circle cx="78" cy="262" r="12" fill="url(#rSkin)" />
    {/* bangles */}
    {[86, 90, 94].map((y, i) => (
      <path key={i} d={`M 74,${248 + i * 4} Q 78,${246 + i * 4} 82,${248 + i * 4}`} stroke="#fbbf24" strokeWidth="1.4" fill="none" />
    ))}
  </svg>
);
